import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

export interface CodeCommon {
  codeid: number;
  codevalue: string;
  codedisplayname: string;
  categoryid: number;
  codediscription: string;
  categoryname: string;
  displayorder: number;
}

// Cache to prevent multiple API calls - keyed by category name
const codeCache: Record<string, CodeCommon[]> = {};
const inflightPromises: Record<string, Promise<CodeCommon[]>> = {};

const useCodeCommon = (categoryName: string, forceRefresh: boolean = false) => {
  const [statusCodes, setStatusCodes] = useState<CodeCommon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCodes = useCallback(async () => {
    if (!categoryName) {
      setStatusCodes([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let codes: CodeCommon[];
      
      const fetchFromAPI = async (): Promise<CodeCommon[]> => {
        const response = await apiService.getAllCodeCommonByCategory(categoryName);
        const raw = (response as any)?.data || response || [];
        return raw as CodeCommon[];
      };
      
      // Check if we need to fetch from API
      const hasCache = codeCache[categoryName] !== undefined;
      const hasInflightPromise = categoryName in inflightPromises;
      const needsFetch = forceRefresh || (!hasCache && !hasInflightPromise);
      
      if (needsFetch) {
        inflightPromises[categoryName] = fetchFromAPI();
        codes = await inflightPromises[categoryName];
        codeCache[categoryName] = codes;
        delete inflightPromises[categoryName];
      } else if (hasCache) {
        codes = codeCache[categoryName];
      } else if (hasInflightPromise) {
        // If already fetching, wait for that promise
        codes = await inflightPromises[categoryName];
      } else {
        // Fallback (shouldn't reach here)
        codes = [];
      }
      
      // Sort by displayorder
      const sortedCodes = [...codes].sort((a: CodeCommon, b: CodeCommon) => 
        a.displayorder - b.displayorder
      );
      
      setStatusCodes(sortedCodes);
    } catch (err: any) {
      console.error(`Error fetching ${categoryName} codes:`, err);
      setError(err.message || `Failed to fetch ${categoryName} options`);
      setStatusCodes([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName, forceRefresh]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  // Helper function to get display name by code value
  const getDisplayNameByCodeValue = useCallback((codeValue: string | number | null | undefined): string => {
    if (codeValue === null || codeValue === undefined) return '';
    const code = statusCodes.find(c => c.codevalue === String(codeValue));
    return code?.codedisplayname || '';
  }, [statusCodes]);

  // Helper function to get code value by display name
  const getCodeValueByDisplayName = useCallback((displayName: string): string | null => {
    const code = statusCodes.find(c => c.codedisplayname === displayName);
    return code?.codevalue || null;
  }, [statusCodes]);

  return { 
    statusCodes, 
    loading, 
    error, 
    refetch: fetchCodes,
    getDisplayNameByCodeValue,
    getCodeValueByDisplayName,
    // Convenience: get options array for dropdowns
    statusOptions: statusCodes.map(code => code.codedisplayname)
  };
};

export default useCodeCommon;

